/**
 * Enhanced fetch with retry logic and timeout for Iran's slow connections
 */

// Default timeout for requests (increased for Iran)
const DEFAULT_TIMEOUT = 60000; // 60 seconds
const SLOW_TIMEOUT = 120000; // 2 minutes for slow connections

/**
 * Check if connection is slow
 */
const isSlowConnection = () => {
  if ('connection' in navigator) {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection && connection.effectiveType) {
      return connection.effectiveType === '2g' || connection.effectiveType === 'slow-2g';
    }
  }
  return false;
};

/**
 * Create AbortController with timeout
 */
const createTimeoutController = (timeout) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  return {
    controller,
    cleanup: () => clearTimeout(timeoutId)
  };
};

/**
 * Enhanced fetch with retry and timeout
 */
export const enhancedFetch = async (url, options = {}, retryCount = 3) => {
  const timeout = isSlowConnection() ? SLOW_TIMEOUT : DEFAULT_TIMEOUT;
  const maxRetries = isSlowConnection() ? 5 : retryCount; // More retries for slow connections
  
  const fetchWithTimeout = async (attempt = 1) => {
    const { controller, cleanup } = createTimeoutController(timeout);
    
    try {
      console.log(`[API] Attempt ${attempt}/${maxRetries} - ${url}`);
      console.log(`[API] Timeout: ${timeout}ms, Slow connection: ${isSlowConnection()}`);
      
      // Get Firebase token for authentication
      const token = localStorage.getItem('firebaseIdToken') || localStorage.getItem('token');
      console.log('🔑 Firebase token:', token ? 'Found' : 'Not found');
      
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          ...options.headers,
        }
      });
      
      cleanup();
      
      // Success
      if (response.ok) {
        console.log(`[API] Success on attempt ${attempt} - ${response.status}`);
        try {
          const data = await response.json();
          console.log(`[API] Parsed JSON data:`, data);
          return data;
        } catch (jsonError) {
          console.error(`[API] Failed to parse JSON:`, jsonError);
          throw new Error('خطا در پردازش پاسخ سرور');
        }
      }
      
      // Server errors (5xx) - retry
      if (response.status >= 500 && attempt < maxRetries) {
        console.log(`[API] Server error ${response.status}, retrying in ${attempt * 1000}ms...`);
        await new Promise(resolve => setTimeout(resolve, attempt * 1000));
        return fetchWithTimeout(attempt + 1);
      }
      
      // Client errors (4xx) - don't retry
      console.log(`[API] Client error ${response.status}, no retry`);
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      
    } catch (error) {
      cleanup();
      
      // AbortError (timeout)
      if (error.name === 'AbortError') {
        console.log(`[API] Timeout on attempt ${attempt}/${maxRetries}`);
        if (attempt < maxRetries) {
          console.log(`[API] Retrying after timeout in ${attempt * 2000}ms...`);
          await new Promise(resolve => setTimeout(resolve, attempt * 2000));
          return fetchWithTimeout(attempt + 1);
        }
        throw new Error(`درخواست پس از ${maxRetries} تلاش ناموفق بود. لطفاً اتصال اینترنت خود را بررسی کنید.`);
      }
      
      // Network errors
      if (error.name === 'TypeError' || error.message.includes('Failed to fetch')) {
        console.log(`[API] Network error on attempt ${attempt}/${maxRetries}:`, error.message);
        if (attempt < maxRetries) {
          console.log(`[API] Retrying after network error in ${attempt * 2000}ms...`);
          await new Promise(resolve => setTimeout(resolve, attempt * 2000));
          return fetchWithTimeout(attempt + 1);
        }
        throw new Error('خطا در اتصال به سرور. لطفاً اتصال اینترنت خود را بررسی کنید.');
      }
      
      // Other errors
      console.error(`[API] Unknown error on attempt ${attempt}:`, error);
      throw error;
    }
  };
  
  return fetchWithTimeout();
};

/**
 * Wrapper for Worker API calls
 */
export const workerFetch = async (endpoint, options = {}, retryCount = 3) => {
  const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://empty-rain-2654.er-hooman.workers.dev';
  const url = endpoint.startsWith('http') ? endpoint : `${baseUrl}${endpoint}`;
  
  console.log(`[Worker API] Calling: ${url}`);
  
  return enhancedFetch(url, options, retryCount);
};

/**
 * Firebase Functions proxy via Worker
 */
export const firebaseFetch = async (functionName, body = {}, options = {}) => {
  const endpoint = `/api/firebase/${functionName}`;
  
  return workerFetch(endpoint, {
    method: 'POST',
    body: JSON.stringify(body),
    ...options
  });
};

/**
 * Utility to add loading states and error handling
 */
export const withLoadingAndError = (apiCall) => {
  return async (setLoading, setError, ...args) => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiCall(...args);
      return result;
    } catch (error) {
      console.error('[API Error]:', error);
      setError(error.message || 'خطای ناشناخته‌ای رخ داد');
      throw error;
    } finally {
      setLoading(false);
    }
  };
};





